import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import LiveVideoPlayer from "./LiveVideoPlayer";
import { useDispatch, useSelector } from "react-redux";
import { DockWrapperContext } from "../../Dock/DockWrapper";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import {
	videoTourInstanceAdded,
	videoTourInstanceRemoved
} from "orion-components/Services/VideoTourClockService/actions";

const cameraPropTypeShape = {
	id: PropTypes.string.isRequired,
	preset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	player: PropTypes.shape({
		type: PropTypes.string.isRequired,
		url: PropTypes.string
	})
};

const propTypes = {
	camera: PropTypes.oneOfType([cameraPropTypeShape, PropTypes.arrayOf(cameraPropTypeShape)]).isRequired,
	instanceId: PropTypes.string,
	fillAvailable: PropTypes.bool,
	entityId: PropTypes.string,
	entityType: PropTypes.string,
	namePrefix: PropTypes.string,
	dockedItemId: PropTypes.string,
	isDockedVideoTour: PropTypes.bool,
	dwellTime: PropTypes.number,
	currentReplayMedia: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	setCameraPriority: PropTypes.func,
	readOnly: PropTypes.bool,
	playbackStartTime: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playBarValue: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playbackPlaying: PropTypes.bool,
	addReplayMedia: PropTypes.func,
	removeReplayMedia: PropTypes.func,
	expanded: PropTypes.bool,
	isOnAlertProfile: PropTypes.bool,
	isVideoWall: PropTypes.bool,
	alertDateTime: PropTypes.any,
	clusterProps: PropTypes.object,
	disableVideoModeToggle: PropTypes.bool
};

const defaultProps = {
	camera: { player: { url: null } },
	instanceId: "",
	entityId: "",
	entityType: "",
	namePrefix: null,
	dockedItemId: null,
	isDockedVideoTour: false,
	fillAvailable: false,
	isOnAlertProfile: false,
	isVideoWall: false,
	isOnProfile: false,
	alertDateTime: "",
	clusterProps: {},
	hideRemoveFromWall: false,
	disableVideoModeToggle: false
};
let isProfileHidden = false;
let isContentWrapperHidden = false;
let isAppbarHidden = false;
let isSettingsAppNavHidden = false;
let fullScreenButtonOldVideoPlayer = false;
let reportsGridHeaderHidden = false;

const hiddenElements = {
	isProfileHidden,
	isContentWrapperHidden,
	isAppbarHidden,
	isSettingsAppNavHidden,
	fullScreenButtonOldVideoPlayer,
	reportsGridHeaderHidden
};

// Function to show elements
function showElements(elementConfigurations) {
	elementConfigurations.forEach(({ key, selector, showStyles }) => {
		if (hiddenElements[key]) {
			document.querySelectorAll(selector).forEach(el => {
				applyStyles(el, showStyles);
			});
			hiddenElements[key] = false;
		}
	});
}

// Function to hide elements
function hideElements(elementConfigurations) {
	elementConfigurations.forEach(({ key, selector, hideStyles }) => {
		if (!hiddenElements[key]) {
			document.querySelectorAll(selector).forEach(el => {
				applyStyles(el, hideStyles);
			});
			hiddenElements[key] = true;
		}
	});
}

// Helper function to apply styles
function applyStyles(element, styles) {
	Object.entries(styles).forEach(([prop, value]) => {
		if (prop.startsWith("--")) {
			element.style.setProperty(prop, value);
		} else {
			element.style[prop] = value;
		}
	});
}

const VideoPlayerContainer = ({
	camera,
	instanceId,
	entityId,
	entityType,
	namePrefix,
	dockedItemId,
	isDockedVideoTour,
	dwellTime,
	fillAvailable,
	readOnly,
	playbackStartTime,
	playBarValue,
	playbackPlaying,
	currentReplayMedia,
	addReplayMedia,
	removeReplayMedia,
	expanded,
	isOnAlertProfile,
	isVideoWall,
	removeFromWall,
	isOnProfile,
	alertDateTime,
	clusterProps,
	hideRemoveFromWall,
	defaultToRecorded,
	playbackStartOffset,
	initialRecordedDuration,
	initialRecordedTimeUnit,
	disableVideoModeToggle
}) => {
	/**
	 * The Video Tour Player Container can correspond to multiple internal players when running video tours.
	 * Hence the instanceId in this container gets appended with the camera index to arrive at the instanceId
	 * for child components. For this reason, all instanceId based component state that pertains to individual
	 * players should be managed in the child hierarchy.
	 */

	const dockData = useSelector(state => state.appState?.dock?.dockData);
	const appId = useSelector(state => state.application.appId);
	const cameraDock = useSelector(state => state.appState?.dock?.cameraDock);
	const WavCamOpen = useSelector(state => state.appState.dock.dockData.WavCam);
	const videoTourPreloadInterval = useSelector(state => getEcoSettingByKey("videoTourPreloadInterval")(state)) || 3;
	const videoTourClockTicks = useSelector(
		state => Array.isArray(camera) && camera.length > 1 && state.videoTourClock?.ticks
	);
	const dockCtx = useContext(DockWrapperContext);
	const handlePanelWidth = dockCtx?.handlePanelWidth ?? (() => {});

	const [currentIndex, setCurrentIndex] = useState(0);
	const [nextIndex, setNextIndex] = useState(-1);
	const [isInFullScreen, setIsInFullScreen] = useState(false);
	const [tourPaused, setTourPaused] = useState(false);
	const [videoPlayerHovered, setVideoPlayerHovered] = useState(false);
	const playerContainerRef = useRef(null);
	const checkIfVideoPlayerInsideAppbar = document.querySelector(".bccVideoPlayerContainer");
	const dispatch = useDispatch();

	const currentIndexRef = useRef(0);

	const cameras = Array.isArray(camera) ? camera : [camera];
	const tourMode = cameras.length > 1;

	useEffect(() => {
		if (tourMode) {
			dispatch(videoTourInstanceAdded(instanceId));
		}

		return () => {
			if (tourMode) {
				dispatch(videoTourInstanceRemoved(instanceId));
			}
		};
	}, [instanceId, tourMode, dispatch]);

	useEffect(() => {
		if (cameras?.length <= 1 || !dwellTime || videoTourClockTicks === null) {
			return;
		}

		if (tourPaused) {
			setNextIndex(-1); // We don't need any preloaded players when current tour is paused
			return;
		}

		if (videoTourClockTicks % dwellTime === 0) {
			currentIndexRef.current = (currentIndexRef.current + 1) % cameras.length;
			setCurrentIndex(currentIndexRef.current);
		}

		let next = -1;
		const nextSwitchTime = dwellTime - (videoTourClockTicks % dwellTime);
		if (nextSwitchTime <= videoTourPreloadInterval) {
			next = (currentIndexRef.current + 1) % cameras.length;
		}
		setNextIndex(next);
	}, [cameras, tourPaused, dwellTime, videoTourPreloadInterval, videoTourClockTicks]);

	const elementConfigurations = [
		{
			key: "isSettingsAppNavHidden",
			selector: ".settings-navigation-panel",
			showStyles: { display: "block" },
			hideStyles: { display: "none" }
		},
		{
			key: "isDockControlHidden",
			selector: ".dock-control.open",
			showStyles: { display: "block", animation: "slideInDock 0.001s ease-in-out 0.001s 1" },
			hideStyles: { display: "none", "--animationSwitch": "none" }
		},
		{
			key: "isProfileHidden",
			selector: ".secondary-panel.list-open",
			showStyles: {
				...(!isOnProfile && { "z-index": "99999" }),
				"--animationSwitch": "0.001s ease-in-out 0.001s 1 slideInProfile"
			},
			hideStyles: {
				...(!isOnProfile && { "z-index": "0" }),
				"--animationSwitch": "none"
			}
		},
		{
			key: "isContentWrapperHidden",
			selector: ".content-wrapper",
			showStyles: { height: `calc(100vh - ${WavCamOpen ? "288px" : "48px"})` },
			hideStyles: { height: "0px" }
		},
		{
			key: "fullScreenButtonOldVideoPlayer",
			selector: ".fullScreenButtonOldVideoPlayer",
			showStyles: { zIndex: "1300" },
			hideStyles: { zIndex: "0" }
		},
		{
			key: "reportsGridHeaderHidden",
			selector:
				".MuiDataGrid-topContainer, .MuiDataGrid-columnHeaders, .MuiDataGrid-row--borderBottom, .MuiDataGrid-scrollbarFiller--header, .MuiDataGrid-filler",
			showStyles: {
				display: "",
				visibility: "",
				opacity: "",
				pointerEvents: "",
				height: "",
				minHeight: ""
			},
			hideStyles: {
				display: "none",
				visibility: "hidden",
				opacity: "0",
				pointerEvents: "none",
				height: "0",
				minHeight: "0"
			}
		},
		{
			key: "isAppbarHidden",
			selector: "#CB2_appBar",
			showStyles: checkIfVideoPlayerInsideAppbar?.closest("#CB2_appBar")
				? {} // don’t apply anything
				: { opacity: "1" },
			hideStyles: checkIfVideoPlayerInsideAppbar?.closest("#CB2_appBar")
				? {} //don’t apply anything
				: { opacity: "0" }
		}
	];

	useEffect(() => {
		return () => {
			if (isOnProfile) {
				const profileSideBar = document.getElementsByClassName("secondary-panel list-open");
				for (let i = 0; i < profileSideBar.length; i++) {
					profileSideBar[i].style.setProperty("--animationSwitch", " 0.5s ease-in-out 0.5s 1 slideInProfile");
				}
			}
			if (isOnAlertProfile) {
				handlePanelWidth(false);
				showElements(elementConfigurations);
			}
		};
	}, [isOnProfile, isOnAlertProfile]);

	const toggleFullScreenCameraDialog = useCallback(async value => {
		if (value) {
			try {
				await playerContainerRef.current?.requestFullscreen?.();
			} catch (error) {
				console.error("Failed to enter browser fullscreen", error);
			}
			return;
		}

		try {
			if (document.fullscreenElement) {
				await document.exitFullscreen?.();
			}
		} catch (error) {
			console.error("Failed to exit browser fullscreen", error);
		}
	}, []);

	const iscameraInDock =
		dockData.isOpen &&
		dockData.tab === "Cameras" &&
		cameraDock.dockedCameras.includes(dockedItemId || cameras[currentIndex]?.id);

	const renderPlayerContent = styles => (
		<div
			style={styles}
			onDoubleClick={() => {
				toggleFullScreenCameraDialog(true);
			}}
			onMouseEnter={() => setVideoPlayerHovered(true)}
			onMouseLeave={() => setVideoPlayerHovered(false)} data-testid="orion-components-cbcomponents-new-video-player-video-player-container-div"
		>
			{cameras?.map((camera, index) => {
				if (index === currentIndex || index === nextIndex) {
					return (
						<div
							key={index}
							className="bcc_header"
							id={`bcc_header_${instanceId}_${index}`}
							style={{
								opacity: index === currentIndex ? 1 : 0,
								...(index === nextIndex && {
									position: "absolute",
									left: 0,
									top: 0
								}),
								...((!iscameraInDock || isVideoWall) && {
									width: "100%",
									height: "100%",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: "#000000"
								})
							}} data-testid="orion-components-cbcomponents-new-video-player-video-player-container-div-2"
						>
							<LiveVideoPlayer
								camera={camera}
								key={`${instanceId}_${index}`}
								instanceId={`${instanceId}_${index}`}
								entityId={entityId}
								entityType={entityType}
								namePrefix={namePrefix}
								dockedItemId={dockedItemId}
								isDockedVideoTour={isDockedVideoTour}
								preload={index === nextIndex}
								tourMode={tourMode}
								tourPaused={tourPaused}
								inDock={iscameraInDock}
								fillAvailable={fillAvailable}
								readOnly={readOnly}
								videoPlayerHovered={videoPlayerHovered}
								playbackStartTime={playbackStartTime}
								playBarValue={playBarValue}
								playbackPlaying={playbackPlaying}
								currentReplayMedia={currentReplayMedia}
								addReplayMedia={addReplayMedia}
								removeReplayMedia={removeReplayMedia}
								expanded={expanded}
								isInFullScreen={isInFullScreen}
								isOnAlertProfile={isOnAlertProfile}
								isInVideoWall={isVideoWall}
								removeFromWall={removeFromWall}
								toggleFullScreenCameraDialog={toggleFullScreenCameraDialog}
								isOnProfile={isOnProfile}
								alertDateTime={alertDateTime}
								dialogContainerRef={playerContainerRef}
								defaultToRecorded={defaultToRecorded}
								playbackStartOffset={playbackStartOffset}
								initialRecordedDuration={initialRecordedDuration}
								initialRecordedTimeUnit={initialRecordedTimeUnit}
								clusterProps={clusterProps}
								hideRemoveFromWall={hideRemoveFromWall}
								disableVideoModeToggle={disableVideoModeToggle}
								setTourPaused={setTourPaused}
							/>
						</div>
					);
				} else {
					return null;
				}
			})}
		</div>
	);

	useEffect(() => {
		const isPlayerFullscreen = document.fullscreenElement === playerContainerRef.current;

		if (isPlayerFullscreen) {
			hideElements(elementConfigurations);

			if (isOnAlertProfile) {
				handlePanelWidth(true);
			}
		} else {
			showElements(elementConfigurations);

			if (isOnAlertProfile) {
				handlePanelWidth(false);
			}
		}
	}, [isInFullScreen, isOnProfile, appId, isOnAlertProfile, handlePanelWidth]);

	useEffect(() => {
		const handleFullscreenChange = () => {
			const activeFullscreenElement = document.fullscreenElement;
			const isPlayerFullscreen = activeFullscreenElement === playerContainerRef.current;
			setIsInFullScreen(isPlayerFullscreen);
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);

	const regularPlayer = (
		<div
			ref={playerContainerRef}
			style={
				isInFullScreen
					? {
							width: "100vw",
							height: "100vh",
							background: "#000000"
						}
					: {
							width: "100%",
							height: "100%"
						}
			}
		>
			{renderPlayerContent({
				width: "100%",
				height: "100%",
				position: "relative"
			})}
		</div>
	);

	return regularPlayer;
};

VideoPlayerContainer.propTypes = propTypes;
VideoPlayerContainer.defaultProps = defaultProps;

export default VideoPlayerContainer;
